/**
 * Owner-view Analysis tab body (tab 3 of 4).
 *
 * Friendly, plain-language analytics rendered inside GenieOwnerDetail — never a
 * dense dashboard. A headline number the owner actually cares about, a simple
 * 7-day bar chart, what callers ask about most, and two calm tiles (resolution
 * rate + how people felt). Read-only viewer: NO editable inputs. The one and
 * only mutating affordance in this flow is the shared "Tell our genie" door.
 *
 * Matches OverviewTab's SectionCard rhythm + spacing so the four owner tabs feel
 * like a single surface. Data: stub `useGenieAnalysis(id)` (PARKED backend).
 * Brand law: genie tokens only; signature gradient for chart accents; wireframe
 * green (Live/Active) -> genie-success; "Help Genie" two words; *Voice* italic;
 * no "AI" language; no em dashes.
 */
import { useParams } from "react-router-dom";
import { GenieDisc } from "@/components/voice-genie/atoms";
import { useGenieAnalysis } from "@/hooks/voice-vision";
import {
  AnalysisHeadline,
  AnalysisSkeleton,
  AnalysisTiles,
  AnalysisTopics,
  AnalysisWeekChart,
} from "./AnalysisSections";

const AnalysisTab = () => {
  const { id = "" } = useParams();
  const { data, isLoading, isError } = useGenieAnalysis(id);

  if (isLoading) return <AnalysisSkeleton />;

  if (isError || !data) {
    return (
      <div className="flex flex-col items-center text-center py-20">
        <div className="mb-4">
          <GenieDisc size={64} />
        </div>
        <h2 className="text-lg font-bold text-[#222B45]">
          We couldn't pull the numbers just yet
        </h2>
        <p className="text-sm text-gray-500 mt-1 max-w-[380px]">
          Your genie's analytics will appear here as soon as conversations start
          rolling in. Please try again in a moment.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <AnalysisHeadline data={data} />
      <AnalysisWeekChart data={data} />
      <AnalysisTopics data={data} />
      <AnalysisTiles data={data} />
    </div>
  );
};

export default AnalysisTab;
